// ------------- import external dependencies -------------
import { useMutation, useQuery } from "@tanstack/react-query";

// ------------ import internal dependencies -------------
import {
  addIncidentSignatory,
  createIncident,
  deleteIncidentFormData,
  getAllIncidentSignatories,
  getAllPickListOptions,
  getIncidentTypes,
  getSavedIncidentDrafts,
  getUsersInFacility,
  saveIncidentFormData,
  UserType,
  getIncidentListByTrends,
  getIncidentsWithParams,
  getSelectedIncidentWithId,
  getIncidentById,
  getdraftIncidentWithId,
  getAttachments,
  deleteAttachment,
  handlePostIncidentAttachment,
  deleteIncidentEndpoint,
  deleteDraftIncidentFromListEndPoint,
  setIncidentStatusToOpen,
  restoreDeletedIncident,
  getSingleAttachments,
  resetIncidentSignatory,
} from "../api/incident/apiIncidents";
import { queryClient } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function useIncident({
  facilityId,
  userId,
  userType,
  incidentId,
  selectedIncidentId,
  idempotencyKey,
  draftId,
  incidentType,
  month,
  year,
  limit,
  offset,
  searchParam,
  startDate,
  endDate,
  status,
  facility,
  attachmentId,
  isDelete,
  isEdit,
}: any = {}) {
  const navigate = useNavigate();

  const createIncidentEntry = useMutation((req: any) => {
    return req.isDraft
      ? createIncident(req.data, idempotencyKey)
      : createIncident(req.data);
  });


  const getAllIncidentsWithParams = useQuery(
    ["incidentsWithParams"],
    () =>
      getIncidentsWithParams({
        userId,
        limit,
        offset,
        searchParam,
        status,
        incidentType,
        facility,
        startDate,
        endDate,
      }),
    {
      enabled: !!userId && !!limit && !!offset,
      keepPreviousData: true,
    }
  );

  const getIncidentDetailsWithId = useQuery(
    ["incidentDetailsWithId"],
    () => getSelectedIncidentWithId(selectedIncidentId),
    {
      enabled: selectedIncidentId?.length > 0,
      keepPreviousData: true,
    }
  );

  const getIncident = useQuery(
    ["incident", incidentId],
    () => getIncidentById(incidentId),
    {
      enabled: !!incidentId,
    }
  );

  const getDraft = useQuery(
    ["draft", draftId],
    () => getdraftIncidentWithId(draftId),
    {
      enabled: !!draftId,
    }
  );

  // facilityId is part of every key below — otherwise switching facilities keeps
  // serving the previous facility's cached list (React Query keys off the array,
  // not the closure's facilityId).
  const getContactsOfTypeInFacility = useQuery(
    ["UsersInFacility", facilityId, userType],
    () => getUsersInFacility(facilityId, userType),
    {
      enabled: !!(facilityId && userType),
    }
  );

  const getStaffInFacility = useQuery(
    ["staffInFacility", facilityId],
    () => getUsersInFacility(facilityId, UserType.staff),
    {
      enabled: !!facilityId,
    }
  );

  const getCaregiversInFacility = useQuery(
    ["caregiversInFacility", facilityId],
    () => getUsersInFacility(facilityId, UserType.caregiver),
    {
      enabled: !!facilityId,
    }
  );

  const getResidentsInFacility = useQuery(
    ["residentsInFacility", facilityId],
    () => getUsersInFacility(facilityId, UserType.resident),
    {
      enabled: !!facilityId,
    }
  );

  const getProvidersInFacility = useQuery(
    ["providersInFacility", facilityId],
    () => getUsersInFacility(facilityId, UserType.provider),
    {
      enabled: !!facilityId,
    }
  );

  const getPickListOptions = useQuery(["pickListOptions"], () =>
    getAllPickListOptions(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes — picklist options rarely change
    }
  );

  const saveIncidentAsDraft = useMutation((req: any) =>
    saveIncidentFormData(req, idempotencyKey)
  );

  const setStatusToOpen = useMutation((req: any) =>
    setIncidentStatusToOpen(req)
  );

  const deleteIncidentDraft = useMutation((req: any) =>
    deleteIncidentFormData(req, idempotencyKey)
  );
  const deleteDraftIncidentFromList = useMutation(
    deleteDraftIncidentFromListEndPoint,
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([
          "incidentsWithParams",
          "userFacilities",
        ]);
        setTimeout(() => window.location.reload(), 1000);
        toast.success(res.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      },
    }
  );

  const getSavedIncidents = useQuery(
    ["incidentDrafts"],
    () =>
      getSavedIncidentDrafts({
        userId,
        facilityId,
        incidentType,
        startDate,
        endDate,
      }),
    {
      enabled: !!userId && !!startDate && !!endDate,
    }
  );

  const getIncidentSignatories = useQuery(
    ["incidentSignatories", incidentId],
    () => getAllIncidentSignatories(incidentId),
    {
      enabled: !!incidentId,
    }
  );

  const addSignatory = useMutation((req: any) => addIncidentSignatory(req));

  const resetSignatory = useMutation((req: string) =>
    resetIncidentSignatory(req)
  );

  const getAllIncidentTypes = useQuery(["incidentTypes"], () =>
    getIncidentTypes()
  );

  const incidentsByTrends = useQuery(
    ["incidentListByTrends", month, year, incidentType, facilityId],
    () =>
      getIncidentListByTrends(
        month as string,
        year as string,
        incidentType as string,
        facilityId as string
      ),
    {
      enabled: !!facilityId && !!month && !!year && !!incidentType,
    }
  );

  const handleGetIncidentAttachments = useQuery(
    ["incidentsAttachments", incidentId],
    () => getAttachments(incidentId as string),
    { enabled: !!incidentId }
  );

  const deleteIncidentAttachment = useMutation((req: any) =>
    deleteAttachment(req, attachmentId)
  );

  const postIncidentAttachment = useMutation((req: any) =>
    handlePostIncidentAttachment(req, incidentId)
  );

  const handleGetSingleAttachments = useQuery(
    ["singleAttachments", attachmentId],
    () => getSingleAttachments(attachmentId),
    { enabled: !!attachmentId }
  );

  const deleteIncident = useMutation(deleteIncidentEndpoint, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["incidentsWithParams", "userFacilities"]);
      navigate("/user/incidents-list");
      setTimeout(() => window.location.reload(), 1000);
      toast.success(res.data.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    },
  });

  const restoreIncident = useMutation(
    ({ incidentId, userId }: any) => restoreDeletedIncident(incidentId, userId),
    {
      onSuccess: (res) => {
        navigate("/user/incidents-list");
        toast.success(res.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      },
      onError: (error: any) => {
        console.log({ error });
        toast.warn(error.message);
      },
    }
  );

  return {
    createIncidentEntry,
    getIncident,
    getDraft,
    getContactsOfTypeInFacility,
    getStaffInFacility,
    getCaregiversInFacility,
    getResidentsInFacility,
    getProvidersInFacility,
    getPickListOptions,
    saveIncidentAsDraft,
    deleteIncidentDraft,
    getSavedIncidents,
    getIncidentSignatories,
    addSignatory,
    resetSignatory,
    getAllIncidentTypes,
    incidentsByTrends,
    getAllIncidentsWithParams,
    getIncidentDetailsWithId,
    handleGetIncidentAttachments,
    deleteIncidentAttachment,
    postIncidentAttachment,
    deleteIncident,
    deleteDraftIncidentFromList,
    setStatusToOpen,
    restoreIncident,
    handleGetSingleAttachments,
  };
}

export default useIncident;
